/**
 * Frontend Security Utilities for NRK Mart
 */

// ── CSRF Token Manager ─────────────────────────────────────────
class CSRFManager {
  constructor() {
    this.token  = null;
    this.expiry = null;
  }

  async getToken(apiUrl) {
    // Return cached token if still valid (cache for 23 h, backend allows 24 h)
    if (this.token && this.expiry && Date.now() < this.expiry) {
      return this.token;
    }
    try {
      const res  = await fetch(`${apiUrl}/api/csrf-token`, { credentials: 'include' });
      const data = await res.json();
      this.token  = data.csrf_token;
      this.expiry = Date.now() + 23 * 60 * 60 * 1000;
      return this.token;
    } catch (err) {
      console.error('Failed to fetch CSRF token:', err);
      return null;
    }
  }

  clearToken() {
    this.token  = null;
    this.expiry = null;
  }
}

export const csrfManager = new CSRFManager();

// ── Secure Session Storage (sessionStorage, obfuscated) ────────
class SecureStorage {
  constructor(type = 'sessionStorage') {
    try {
      this._store = window[type];
    } catch {
      this._store = null;
    }
  }

  _encode(data) { try { return btoa(JSON.stringify(data)); } catch { return null; } }
  _decode(raw)  { try { return JSON.parse(atob(raw)); }      catch { return null; } }

  set(key, value) {
    if (!this._store) return false;
    try {
      const encoded = this._encode(value);
      if (encoded) this._store.setItem(key, encoded);
      return !!encoded;
    } catch { return false; }
  }

  get(key) {
    if (!this._store) return null;
    try {
      const raw = this._store.getItem(key);
      return raw ? this._decode(raw) : null;
    } catch { return null; }
  }

  remove(key) {
    if (!this._store) return false;
    try { this._store.removeItem(key); return true; } catch { return false; }
  }
}

export const secureStorage = new SecureStorage('sessionStorage');

// ── Secure Axios Setup ─────────────────────────────────────────
// Call this once in App.js to attach CSRF token to all non-GET requests.
export const createSecureAxios = (axios, apiUrl) => {
  axios.interceptors.request.use(async (config) => {
    config.headers = config.headers || {};
    // Attach customer JWT when present — backend still accepts unauthenticated
    // checkout for WhatsApp/guest, but logged-in sessions get bound to the token.
    try {
      if (!config.headers.Authorization) {
        const user = secureStorage.get('nrk_user');
        if (user?.token) config.headers.Authorization = `Bearer ${user.token}`;
      }
    } catch { /* ignore decode errors */ }

    if (['get', 'head', 'options'].includes(config.method?.toLowerCase())) return config;
    const token = await csrfManager.getToken(apiUrl);
    if (token) config.headers['X-CSRF-Token'] = token;
    return config;
  }, (err) => Promise.reject(err));

  axios.interceptors.response.use(
    (res) => res,
    async (err) => {
      if (err.response?.status === 403 && err.response?.data?.code === 'CSRF_INVALID') {
        csrfManager.clearToken();
        const config = err.config;
        if (!config._retry) {
          config._retry = true;
          const newToken = await csrfManager.getToken(apiUrl);
          if (newToken) {
            config.headers['X-CSRF-Token'] = newToken;
            return axios.request(config);
          }
        }
      }
      return Promise.reject(err);
    }
  );

  return axios;
};

// ── Input Validators ───────────────────────────────────────────
export const validators = {
  isValidPhone(phone) {
    if (!phone) return false;
    const digits = String(phone).replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 12 && digits.startsWith('91'));
  },

  isValidOTP(otp) { return /^\d{6}$/.test(String(otp)); },

  isValidName(name) {
    if (!name || name.length < 2 || name.length > 100) return false;
    return /^[a-zA-Z\s.''-]+$/.test(name);
  },

  isValidAmount(amount) {
    const num = parseFloat(amount);
    return !isNaN(num) && num > 0 && num < 1000000;
  },

  sanitizeString(input, maxLength = 1000) {
    if (typeof input !== 'string') return '';
    return input.trim().slice(0, maxLength).replace(/\0/g, '').replace(/[\x00-\x1F\x7F]/g, '');
  },
};

// ── Content Security Helpers ───────────────────────────────────
export const contentSecurity = {
  escapeHTML(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },

  sanitizeURL(url) {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol) ? url : '#';
    } catch { return '#'; }
  },
};
